import { createSlice } from '@reduxjs/toolkit';
import { LearningPathwayState, LearningPathwayStateData } from 'src/types/state/learningPathway';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { suApiSlice } from './api/su';
import { LearningPathwayStaticApiData } from '../../types/api/learningPathwayStatic';
import type { AppState } from '../store';

// State of the Learning Pathways Slice
export interface LearningPathwaysSliceState {
  byId: Record<string, LearningPathwayState>
  // Store for additional learning pathway metadata
}

export const initialState: LearningPathwaysSliceState = {
  byId: {},
};

export const learningPathwaysSlice = createSlice({
  name: 'learningPathways',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Helper function to process learning pathway data into state
    const processLearningPathwayData = (
      state: LearningPathwaysSliceState,
      learningPathwayData: LearningPathwayStaticApiData,
    ) => {
      try {
        if (!learningPathwayData || !learningPathwayData.learningPathwayId) {
          ClientLogger.error(ClientLog.IllegalInternalDataState, {
            payload: learningPathwayData,
            url: window.location.href,
          });
          return;
        }

        const processedSections = learningPathwayData.sections?.map((section) => ({
          ...section,
          learningModuleIds: section.moduleList?.map((module) => module.moduleId) || [],
        }));

        const existingData = state.byId[learningPathwayData.learningPathwayId]?.data;

        // A payload carrying a sections array is authoritative for both sections and stats;
        // a payload omitting sections is partial (list/preview/metadata-only) and must not
        // clobber cached sections or drop cached counts.
        // The backend's full transform() always sets sections (even to []), while
        // transformToMetadataOnly (used by list/carousel/preview) never sets it — so the
        // presence of a sections array marks a full/authoritative payload.
        const isFullPayload = Array.isArray(learningPathwayData.sections);

        // Full payload: processedSections is defined (possibly []) and replaces cached sections.
        // Partial payload: keep cached sections (an explicit array, including [], overwrites).
        const sections = processedSections ?? existingData?.sections ?? [];

        // Full payload REPLACES stats (authoritative), so stale preview counts cannot outlive
        // a full fetch that reports zero content; partial payload merges so cached counts
        // (totalSections/totalModules/etc.) survive, with incoming keys winning.
        const learningPathwayStats = isFullPayload
          ? learningPathwayData.learningPathwayStats
          : {
            ...existingData?.learningPathwayStats,
            ...learningPathwayData.learningPathwayStats,
          };

        const stateData: LearningPathwayStateData = {
          ...learningPathwayData,
          imageSrc: learningPathwayData.backgroundImage,
          sections,
          learningPathwayStats,
        };

        state.byId[learningPathwayData.learningPathwayId] = { data: stateData };
      } catch (error) {
        ClientLogger.error(ClientLog.PayloadProcessingError, {
          learningPathwayId: learningPathwayData.learningPathwayId,
          url: window.location.href,
        });
      }
    };

    builder
      .addMatcher(
        suApiSlice.endpoints.getLearningPathway.matchFulfilled,
        (state, action) => {
          // Payloads that omit sections carry only partial stats; preserve
          // already-cached sections and merge stats so the LP page keeps its modules AND
          // its section/module counts, while still populating byId so the topic banner
          // renders on a cold cache.
          processLearningPathwayData(state, action.payload.learningPathway);
        },
      )
      .addMatcher(
        suApiSlice.endpoints.getCustomerCredential.matchFulfilled,
        (state, action) => {
          try {
            processLearningPathwayData(state, action.payload.customerCredential.credential.learningPathway);
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, {
              actionType: action.type,
              url: window.location.href,
            });
          }
        },
      )
      .addMatcher(
        suApiSlice.endpoints.getLearningPathwayStatic.matchFulfilled,
        (state, action) => {
          // Process learning pathway data into our store from the GET LearningPathwayStatic API
          const getLearningPathwaysById = (learningPathwayData: LearningPathwayStaticApiData) => {
            try {
              const learningPathwaysById: Record<string, LearningPathwayState> = {};

              // Validate required fields
              if (!learningPathwayData || !learningPathwayData.learningPathwayId) {
                ClientLogger.error(ClientLog.IllegalInternalDataState, {
                  payload: learningPathwayData,
                  url: window.location.href,
                });
                return learningPathwaysById;
              }

              const stateData: LearningPathwayStateData = {
                ...learningPathwayData,
                imageSrc: learningPathwayData.backgroundImage,
                sections: learningPathwayData.sections?.map((section) => {
                  const learningModuleIds = section.moduleList
                    ?.map((module) => module.moduleId) || [];

                  return {
                    ...section,
                    learningModuleIds,
                  };
                }) || [],
              };

              // Store the pathway data
              learningPathwaysById[learningPathwayData.learningPathwayId] = {
                data: stateData,
              };

              return learningPathwaysById;
            } catch (error) {
              ClientLogger.error(ClientLog.PayloadProcessingError, {
                learningPathwayId: learningPathwayData.learningPathwayId,
                url: window.location.href,
              });
              return {};
            }
          };

          state.byId = {
            ...state.byId,
            ...getLearningPathwaysById(action.payload.learningPathway),
          };
        },
      )
      .addMatcher(
        suApiSlice.endpoints.listLearningPathways.matchFulfilled,
        (state, action) => {
          // Process learning pathways list data into our store
          try {
            action.payload.learningPathways.forEach((pathwayData: LearningPathwayStaticApiData) => {
              processLearningPathwayData(state, pathwayData);
            });
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, {
              url: window.location.href,
            });
          }
        },
      )
      .addMatcher(
        suApiSlice.endpoints.listLearningPathwaysPoly.matchFulfilled,
        (state, action) => {
          // Process PolyD learning pathways list data into our store
          try {
            action.payload.learningPathways.forEach((pathwayData: LearningPathwayStaticApiData) => {
              processLearningPathwayData(state, pathwayData);
            });
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, { url: window.location.href });
          }
        },
      );
    // Add more matchers that may react to another slice's actions/APIs
  },
});

// Basic selectors
export const learningPathwaysActions = learningPathwaysSlice.actions;
export const selectLearningPathwayById = (
  state: AppState,
  learningPathwayId: string,
) => {
  return state.learningPathways.byId[learningPathwayId];
};

// Selectors for learning pathways list
export const selectAllLearningPathways = (state: AppState) => {
  return Object.values(state.learningPathways.byId);
};
